import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api from '../../lib/api';
import { toast } from 'sonner';
import { 
  Shield, 
  Save,
  Package,
  FileText,
  Truck,
  PackageCheck,
  LayoutDashboard,
  Database,
  Settings as SettingsIcon,
  Send,
  Eye,
  Plus,
  Edit,
  Trash2,
  Download,
  MessageCircle,
  RefreshCw
} from 'lucide-react';

const MODULES = [
  { id: 'grey_orders', name: 'Grey Orders', icon: Package },
  { id: 'grey_bills', name: 'Grey Bills', icon: FileText },
  { id: 'lr_reports', name: 'LR Reports', icon: Truck },
  { id: 'grey_receipts', name: 'Grey Receipts', icon: PackageCheck },
  { id: 'lr_issue', name: 'LR Issue', icon: Send },
  { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
  { id: 'reports', name: 'Reports', icon: FileText },
  { id: 'masters', name: 'Masters', icon: Database },
  { id: 'settings', name: 'Settings', icon: SettingsIcon }
];

const MODULE_PERMISSIONS = [
  { key: 'view', label: 'View', icon: Eye },
  { key: 'create', label: 'Create', icon: Plus },
  { key: 'edit', label: 'Edit', icon: Edit },
  { key: 'delete', label: 'Delete', icon: Trash2 },
  { key: 'export_pdf', label: 'Export PDF', icon: Download },
  { key: 'send_whatsapp', label: 'Send WhatsApp', icon: MessageCircle }
];

const ADVANCED_PERMISSIONS = [
  { key: 'edit_after_save', label: 'Edit After Save', description: 'Allow editing records after initial save' },
  { key: 'reopen_completed', label: 'Reopen Completed Records', description: 'Allow reopening completed orders/records' },
  { key: 'override_validation', label: 'Override Validation Rules', description: 'Bypass validation checks when needed' },
  { key: 'issue_lr', label: 'Issue LR', description: 'Can issue LRs for delivery' },
  { key: 'bulk_lr_issue', label: 'Bulk LR Issue', description: 'Can issue multiple LRs at once' },
  { key: 'view_rates', label: 'View Rates', description: 'Can view rate information' },
  { key: 'edit_rates', label: 'Edit Rates', description: 'Can modify rate information' },
  { key: 'export_reports', label: 'Export Reports', description: 'Can export report data' },
  { key: 'manage_masters', label: 'Manage Masters', description: 'Full access to master data management' },
  { key: 'access_settings', label: 'Access Settings', description: 'Can view and modify system settings' },
  { key: 'access_admin_panel', label: 'Access Admin Panel', description: 'Can access the Admin Panel section' }
];

const AccessControl = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchPermissions(selectedRole);
    }
  }, [selectedRole]);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/roles');
      setRoles(response.data);
      if (response.data.length > 0) {
        setSelectedRole(response.data[0].role_id);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load roles');
      setLoading(false);
    }
  };

  const fetchPermissions = async (roleId) => {
    try {
      const response = await api.get(`/admin/permissions/${roleId}`);
      setPermissions(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load permissions');
    }
  };

  const updateModulePermission = (moduleId, permKey, value) => {
    if (!permissions) return;
    
    const updatedModulePerms = permissions.module_permissions.map(mp => {
      if (mp.module === moduleId) {
        return { ...mp, [permKey]: value };
      }
      return mp;
    });
    
    setPermissions({ ...permissions, module_permissions: updatedModulePerms });
    setHasChanges(true);
  };

  const updateAdvancedPermission = (permKey, value) => {
    if (!permissions) return;
    
    setPermissions({
      ...permissions,
      advanced_permissions: {
        ...permissions.advanced_permissions,
        [permKey]: value
      }
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/admin/permissions/${selectedRole}`, permissions);
      toast.success('Permissions saved successfully');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchPermissions(selectedRole);
  };

  const getModulePermission = (moduleId, permKey) => {
    if (!permissions) return false;
    const mp = permissions.module_permissions.find(m => m.module === moduleId);
    return mp ? mp[permKey] : false;
  };

  const getSelectedRoleName = () => {
    const role = roles.find(r => r.role_id === selectedRole);
    return role?.role_name || '';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="access-control-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Access Control
            </h1>
            <p className="text-slate-600 mt-1">Configure module-based permissions for each role</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map(role => (
                  <SelectItem key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasChanges && (
              <>
                <Button variant="outline" onClick={handleReset}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Role Badge */}
      <div className="mb-6 p-4 bg-slate-50 rounded-lg border flex items-center gap-3">
        <Shield className="h-6 w-6 text-slate-600" />
        <div>
          <p className="text-sm text-slate-500">Configuring permissions for</p>
          <p className="text-lg font-semibold text-slate-900">{getSelectedRoleName()}</p>
        </div>
        {hasChanges && (
          <Badge className="ml-auto bg-amber-100 text-amber-700 border-amber-200">Unsaved Changes</Badge>
        )}
      </div>

      {permissions && (
        <Tabs defaultValue="modules" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-96">
            <TabsTrigger value="modules">Module Permissions</TabsTrigger>
            <TabsTrigger value="advanced">Advanced Permissions</TabsTrigger>
          </TabsList>

          {/* Module Permissions Tab */}
          <TabsContent value="modules" className="space-y-4">
            {MODULES.map(module => {
              const Icon = module.icon;
              return (
                <Card key={module.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Icon className="h-5 w-5 text-slate-600" />
                      {module.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {MODULE_PERMISSIONS.map(perm => {
                        const PermIcon = perm.icon;
                        const isChecked = getModulePermission(module.id, perm.key);
                        return (
                          <div key={perm.key} className="flex items-center space-x-2">
                            <Switch
                              id={`${module.id}-${perm.key}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => updateModulePermission(module.id, perm.key, checked)}
                            />
                            <Label 
                              htmlFor={`${module.id}-${perm.key}`}
                              className="text-sm flex items-center gap-1 cursor-pointer"
                            >
                              <PermIcon className="h-3.5 w-3.5 text-slate-400" />
                              {perm.label}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* Advanced Permissions Tab */}
          <TabsContent value="advanced" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Advanced Operational Permissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ADVANCED_PERMISSIONS.map(perm => (
                    <div 
                      key={perm.key} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex-1">
                        <Label htmlFor={perm.key} className="text-sm font-medium cursor-pointer">
                          {perm.label}
                        </Label>
                        <p className="text-xs text-slate-500 mt-0.5">{perm.description}</p>
                      </div>
                      <Switch
                        id={perm.key}
                        checked={permissions.advanced_permissions[perm.key] || false}
                        onCheckedChange={(checked) => updateAdvancedPermission(perm.key, checked)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AccessControl;
